/**
 * @fileoverview A class's curriculum, kept as revisions (O10, I8).
 *
 * The organization and the class's instructors both read every revision and
 * both may add one, labelled by who uploaded it so the two can be told apart.
 * Newest first, because "what are we teaching now" is the usual question and
 * "what did we teach in October" is the reason the older ones stay.
 *
 * Decision D6: the class keeps twenty revisions and the twenty-first is
 * refused, never the oldest pruned. Delete is explicit: an org admin may
 * remove any revision on their class, an instructor only one they uploaded.
 *
 * The page owns the list (`materials`) and reloads it through `onChanged`;
 * this card only draws it and drives the upload and delete.
 */

import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button, Card, Icon, ListRow, SectionHeader, Tag } from "@/components/studio/primitives";
import {
  CURRICULUM,
  MATERIAL_ACCEPT,
  MATERIAL_HINT,
  MAX_CURRICULUM_REVISIONS,
  curriculumRevisions,
  uploadMaterial,
  uploadedOn,
  uploaderLabel,
  validateMaterial,
} from "@/lib/materials";
import { formatBytes } from "@/lib/attachments";

/**
 * @param {{
 *   client: object, basePath: string, role: "org_admin"|"instructor", actorId?: string,
 *   materials: Array, onChanged: Function, errorMessage: Function, id?: string, className?: string,
 * }} props
 */
export default function CurriculumCard({
  client,
  basePath,
  role,
  actorId,
  materials,
  onChanged,
  errorMessage = (_err, fallback) => fallback,
  id = "curriculum",
  className = "",
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(null);
  const [removing, setRemoving] = useState(null);
  const revisions = curriculumRevisions(materials);
  const full = revisions.length >= MAX_CURRICULUM_REVISIONS;
  const isOrg = role === "org_admin";

  const canDelete = (m) => isOrg || (actorId && m.uploaded_by === actorId);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const problem = validateMaterial(file);
    if (problem) {
      toast.error(problem);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading({ name: file.name, percent: 0 });
    try {
      await uploadMaterial(client, basePath, file, {
        scope: CURRICULUM,
        onProgress: (percent) => setUploading({ name: file.name, percent }),
      });
      toast.success("Curriculum uploaded");
      await onChanged?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't upload that file"));
    } finally {
      setUploading(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.filename}? The other revisions stay.`)) return;
    setRemoving(m.material_id);
    try {
      await client.delete(`${basePath}/materials/${m.material_id}`);
      toast.success("Revision deleted");
      await onChanged?.();
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't delete that revision"));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div id={id} className={className} data-testid="curriculum-card">
      <SectionHeader
        action={full ? undefined : isOrg ? "Upload revision" : "Upload your revision"}
        onAction={() => inputRef.current?.click()}
        actionTestId="curriculum-upload"
      >
        Curriculum{revisions.length ? ` (${revisions.length})` : ""}
      </SectionHeader>
      <input
        ref={inputRef}
        type="file"
        accept={MATERIAL_ACCEPT}
        onChange={pick}
        className="hidden"
        aria-label="Upload a curriculum revision"
        data-testid="curriculum-input"
      />
      <Card padding={0}>
        {uploading && (
          <div className="border-b border-[var(--studio-divider)] px-[18px] py-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="curriculum-uploading">
            Uploading {uploading.name}… {uploading.percent}%
          </div>
        )}
        {/* Refused, not pruned: the oldest revision is exactly what someone
            goes looking for a year later. */}
        {full && (
          <div className="border-b border-[var(--studio-divider)] px-[18px] py-3 font-sans text-[13px] text-[var(--text-secondary)]" data-testid="curriculum-full">
            This class keeps {MAX_CURRICULUM_REVISIONS} revisions. Delete one you no longer need to upload another.
          </div>
        )}
        {revisions.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="curriculum-empty">
            {isOrg
              ? "No curriculum yet. Upload what this class teaches; every upload keeps the earlier ones as revisions."
              : "The organization hasn't uploaded a curriculum for this class yet. You can upload your own; it is labelled as yours."}
            <span className="mt-1 block text-[13px]">{MATERIAL_HINT}</span>
          </div>
        ) : (
          revisions.map((m, i) => (
            <ListRow
              key={m.material_id}
              borderBottom={i < revisions.length - 1}
              data-testid={`curriculum-row-${m.material_id}`}
              title={m.filename}
              subtitle={[
                `Revision ${m.revision}`,
                uploaderLabel(m),
                uploadedOn(m.uploaded_at),
                m.size_bytes ? formatBytes(m.size_bytes) : null,
                m.note,
              ]
                .filter(Boolean)
                .join(" · ")}
              trailing={
                <span className="flex items-center gap-2">
                  {i === 0 && <Tag accent>Current</Tag>}
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Download ${m.filename}`}
                    data-testid={`curriculum-download-${m.material_id}`}
                    className="rounded-full p-2 text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
                  >
                    <Icon name="download" size={16} />
                  </a>
                  {canDelete(m) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Delete ${m.filename}`}
                      disabled={removing === m.material_id}
                      onPress={() => remove(m)}
                      data-testid={`curriculum-delete-${m.material_id}`}
                    >
                      <Icon name="trash" size={15} />
                    </Button>
                  )}
                </span>
              }
            />
          ))
        )}
      </Card>
    </div>
  );
}
